export const BLANKS_LABEL = "(Blanks)";

/**
 * Normalises a set-filter value the same way AG Grid labels it.
 * @param {*} v Raw cell/filter value.
 * @returns {string} "(Blanks)" for null/undefined/empty, otherwise String(v).
 */
export const normalizeFilterValue = (v) =>
    v === null || v === undefined || v === "" ? BLANKS_LABEL : String(v);

/**
 * Lists the selected values behind one column's AG Grid filter model.
 * Set filters expose `values`; text/number/date filters expose conditions.
 * @param {object} filterModel Filter model for a single column.
 * @returns {string[]}
 */
export const getFilterSelectionValues = (filterModel) => {
    if (!filterModel) return [];
    if (Array.isArray(filterModel.values)) {
        return filterModel.values.map(normalizeFilterValue);
    }
    const conditions = Array.isArray(filterModel.conditions)
        ? filterModel.conditions
        : [filterModel.condition1, filterModel.condition2].filter(Boolean);
    const source = conditions.length > 0 ? conditions : [filterModel];
    return source
        .map((c) => c?.filter ?? c?.dateFrom ?? "")
        .filter((v) => v !== "" && v != null)
        .map(String);
};

/**
 * Builds a field -> Set of normalised options map from fixed option lists.
 * Must mirror each column's `filterParams.values`.
 * @param {Object<string, Array<*>|undefined>} lists
 * @returns {Object<string, Set<string>>}
 */
export const buildStaticOptionSets = (lists = {}) =>
    Object.fromEntries(
        Object.entries(lists).map(([k, v]) => [k, new Set((v || []).map(normalizeFilterValue))])
    );

/**
 * Returns a copy of the filter model without the given column ids.
 * @param {object} filterModel
 * @param {string[]} colIds
 * @returns {object}
 */
export const omitFilterColumns = (filterModel, colIds) => {
    const next = { ...(filterModel || {}) };
    colIds.forEach((id) => delete next[id]);
    return next;
};

/**
 * The options a set-filter column's popup lists, given the OTHER active column filters. Used two ways:
 * the FIXED-LIST columns' `values` callback calls it so their popup lists exactly these; and
 * buildFilterChips calls it (for every set column) to get that available set — to tell when the user has
 * ticked all of them ("select all") and whether "(Blanks)" is still a shown option. Grid-derived columns
 * narrow their popups natively, but the chip logic still needs their available set from here.
 * - When no other set filter is active it returns the column's full option list: its static `values`
 *   list (when supplied) UNION every value present in the data, incl. "(Blanks)".
 * - When another set filter IS active it returns only the values that remain on screen under those
 *   filters — the narrowed list AG Grid displays — so ticking every one of them excludes nothing.
 * A column never narrows its own option list (so you can re-tick values you cleared).
 * @param {object} api AG Grid API (needs forEachNode / getFilterModel / getColumnDef).
 * @param {object} [context] Grid context passed to filterValueGetter.
 * @param {string} colId Target column id.
 * @param {Object<string, Set<string>>} [staticOptionSets] Fixed option lists keyed by field.
 * @returns {string[]} Normalised option labels.
 */
export const getSetFilterValues = (api, context, colId, staticOptionSets = {}) => {
    if (!api?.forEachNode) return [];
    const colDef = api.getColumnDef?.(colId) || {};
    const field = colDef.field || colId;

    // Normalised value a set filter sees for a row in a column (honours filterValueGetter).
    const rowValue = (def, id, node) => {
        const raw = typeof def.filterValueGetter === "function"
            ? def.filterValueGetter({ data: node.data, node, colDef: def, api, context, getValue: (f) => node.data?.[f] })
            : node.data?.[def.field || id];
        return normalizeFilterValue(raw);
    };

    // Other active SET filters; a column does not narrow its own option list.
    const model = api.getFilterModel?.() || {};
    const others = Object.keys(model)
        .filter((id) => id !== colId && Array.isArray(model[id]?.values))
        .map((id) => ({ id, def: api.getColumnDef?.(id) || {}, selected: new Set(model[id].values.map(normalizeFilterValue)) }));

    // Values this column takes across the rows that pass those other filters.
    const present = new Set();
    api.forEachNode((node) => {
        if (!node?.data || node.group) return;
        if (others.every((o) => o.selected.has(rowValue(o.def, o.id, node)))) {
            present.add(rowValue(colDef, colId, node));
        }
    });

    // No other filter: show the full option list (static list UNION every data value). With another
    // filter active, show only what remains on screen under it.
    if (others.length === 0) {
        const universe = new Set(present);
        const staticOptions = staticOptionSets[field];
        if (staticOptions) staticOptions.forEach((v) => universe.add(v));
        return [...universe];
    }
    return [...present];
};

/**
 * Every distinct normalised value a column can take across ALL rows, ignoring the active filters, unioned
 * with its static option list. This is the full set a set-filter popup can show: AG Grid keeps a value
 * visible once it has been selected even after another filter leaves it with no rows, so the popup can
 * list more than getSetFilterValues (which narrows by the other active filters). buildFilterChips uses
 * this only as the FALLBACK "select all" threshold, when AG Grid's own "(Select All)" verdict
 * (everythingVisibleSelectedFromInstance, passed in by the hook) is unavailable — the selection must
 * cover this whole set — so a column is never wrongly cleared while an option is still unchecked on screen.
 * @param {object} api AG Grid API (needs forEachNode / getColumnDef).
 * @param {object} [context] Grid context passed to filterValueGetter.
 * @param {string} colId Target column id.
 * @param {Object<string, Set<string>>} [staticOptionSets] Fixed option lists keyed by field.
 * @returns {string[]} Normalised option labels.
 */
export const getAllColumnValues = (api, context, colId, staticOptionSets = {}) => {
    if (!api?.forEachNode) return [];
    const colDef = api.getColumnDef?.(colId) || {};
    const field = colDef.field || colId;
    const values = new Set();
    api.forEachNode((node) => {
        if (!node?.data || node.group) return;
        const raw = typeof colDef.filterValueGetter === "function"
            ? colDef.filterValueGetter({ data: node.data, node, colDef, api, context, getValue: (f) => node.data?.[f] })
            : node.data?.[field];
        values.add(normalizeFilterValue(raw));
    });
    const staticOptions = staticOptionSets[field];
    if (staticOptions) staticOptions.forEach((v) => values.add(v));
    return [...values];
};

/**
 * Set-filter columns whose selection has GROWN since the previous filter model — the only columns a
 * "select all" can apply to (the user added ticks). A newly activated column (absent from prevFilterModel)
 * never qualifies, and neither does an unchecking step. Text/number/date columns (no `values` array) are
 * ignored. The hook uses this to decide which columns to ask AG Grid about.
 * @param {object} filterModel Current grid filter model.
 * @param {object} [prevFilterModel] Filter model from the previous event.
 * @returns {string[]} Column ids whose set selection grew.
 */
export const getGrownSetColumns = (filterModel, prevFilterModel = null) => {
    const current = filterModel || {};
    const prev = prevFilterModel || {};
    return Object.keys(current).filter((id) => {
        if (!Array.isArray(current[id]?.values) || !Array.isArray(prev[id]?.values)) return false;
        const prevSize = new Set(prev[id].values.map(normalizeFilterValue)).size;
        const curSize = new Set(current[id].values.map(normalizeFilterValue)).size;
        return curSize > prevSize;
    });
};

/**
 * Reads AG Grid's own "is every displayed (visible) value selected" flag from a set-filter instance (as
 * returned by `api.getColumnFilterInstance`). This is the exact signal AG Grid uses to tick its
 * "(Select All)" checkbox, so it matches the popup precisely: the popup shows the values available under
 * the other active filters plus any AG Grid keeps visible after they lose rows, a set that depends on AG
 * Grid's own interaction state and that neither getSetFilterValues (narrowed) nor getAllColumnValues
 * (full) can reproduce. Returns null when the signal is unreachable (not a set filter, or an AG Grid
 * change) so callers can fall back to a value-set heuristic.
 * NOTE: reaches into AG Grid internals (instance.handler.valueModel.isEverythingVisibleSelected()),
 * verified against the pinned ag-grid 35.2.1, and wrapped so any change degrades gracefully.
 * @param {object} instance Set-filter component instance.
 * @returns {boolean|null}
 */
export const everythingVisibleSelectedFromInstance = (instance) => {
    try {
        const valueModel = instance?.handler?.valueModel ?? instance?.valueModel;
        if (valueModel && typeof valueModel.isEverythingVisibleSelected === "function") {
            return !!valueModel.isEverythingVisibleSelected();
        }
        if (typeof instance?.isEverythingVisibleSelected === "function") {
            return !!instance.isEverythingVisibleSelected();
        }
    } catch (e) {
        // AG Grid internals changed or unavailable — let the caller fall back to the value-set check.
    }
    return null;
};

/**
 * Builds applied-filter chips from the grid's current filter state.
 * For a SET-filter column the chip lists each checked value, with ONE exception: the "(Blanks)"
 * pseudo-value is dropped when another active filter has hidden it (no blank rows remain under those
 * filters, so its popup checkbox is gone). Real values stay even when another filter hides them — an
 * explicit pick (e.g. a partner with no rows under the other filters) still shows and counts. So the
 * chip matches the popup's checkboxes except that it never counts a "(Blanks)" the popup no longer
 * offers. Text/number/date columns aren't narrowed; their chip lists each condition.
 * "Select all" is handled separately: when the user has just GROWN a set filter's selection (versus
 * `prevFilterModel`) to tick every option the popup shows, the column is returned in `selectAllColIds`
 * (the caller drops it from the model, clearing the blue header icon) and shows no chip. Whether every
 * shown option is ticked is decided by `isSelectAllColumn(colId)` — the caller passes AG Grid's own
 * "(Select All)" verdict here (true/false). When that returns null/undefined (AG Grid could not answer)
 * it falls back to the full distinct value set (getAllColumnValues), never the narrowed set. Requiring
 * GROWTH means an incidental narrowing never drops a deliberate selection, and unchecking never trips it.
 * @param {object} api AG Grid API.
 * @param {object} [context] Grid context passed to filterValueGetter.
 * @param {Object<string, Set<string>>} [staticOptionSets] Fixed option lists keyed by field.
 * @param {object} [prevFilterModel] Filter model from the previous event; a column can only become
 *   "select all" if it was already an active set filter here with fewer values selected.
 * @param {(colId:string)=>(boolean|null)} [isSelectAllColumn] Authoritative "every shown option ticked"
 *   verdict per column (AG Grid's signal); null/undefined means unknown -> use the value-set fallback.
 * @returns {{ chips: Array<{colId:string,label:string,count:number,values:string[]}>, selectAllColIds: string[], filterModel: object }}
 */
export const buildFilterChips = (api, context, staticOptionSets = {}, prevFilterModel = null, isSelectAllColumn = null) => {
    const filterModel = api?.getFilterModel?.() || {};
    const colIds = Object.keys(filterModel);
    const setColIds = colIds.filter((id) => Array.isArray(filterModel[id]?.values));
    const colDefs = Object.fromEntries(colIds.map((id) => [id, api.getColumnDef?.(id) || {}]));
    const selectedSets = Object.fromEntries(
        setColIds.map((id) => [id, new Set(filterModel[id].values.map(normalizeFilterValue))])
    );

    // "Select all" is compared against prevFilterModel so only a GROWN selection can trip it — never a
    // newly activated column or an unchecking step.
    const prev = prevFilterModel || {};
    const prevSelectedSize = (colId) =>
        Array.isArray(prev[colId]?.values) ? new Set(prev[colId].values.map(normalizeFilterValue)).size : null;

    const chips = [];
    const selectAllColIds = [];
    colIds.forEach((colId) => {
        const def = colDefs[colId];
        const values = getFilterSelectionValues(filterModel[colId]);
        if (!values.length) return;

        // Text/number/date columns aren't narrowed; list their conditions as-is.
        if (!selectedSets[colId]) {
            chips.push({ colId, label: def.headerName || colId, count: values.length, values });
            return;
        }

        // "Select all": the user just GREW this column's selection to tick every option on screen, so the
        // filter excludes nothing and the column is dropped (clearing its blue header icon). GROWTH is
        // required so an incidental narrowing by another filter never silently drops a deliberate
        // selection. Whether every shown option is ticked is AG Grid's own verdict (isSelectAllColumn,
        // its "(Select All)" signal) because the popup shows a set that neither the narrowed nor the full
        // value list reproduces exactly; only when AG Grid can't answer (null) do we fall back to the full
        // distinct value set (getAllColumnValues), never the narrowed one (which would clear while a
        // hidden-but-visible option is still unchecked).
        const before = prevSelectedSize(colId);
        if (before !== null && selectedSets[colId].size > before) {
            const verdict = typeof isSelectAllColumn === "function" ? isSelectAllColumn(colId) : null;
            const isSelectAll = verdict === true || verdict === false
                ? verdict
                : (() => {
                    const allValues = getAllColumnValues(api, context, colId, staticOptionSets);
                    return allValues.length > 0 && allValues.every((v) => selectedSets[colId].has(v));
                })();
            if (isSelectAll) {
                selectAllColIds.push(colId);
                return;
            }
        }

        // Keep every checked value EXCEPT "(Blanks)" when the other filters have hidden it (no blank rows
        // remain, so its checkbox is gone). Real values stay even when hidden — an explicit pick such as
        // a partner with no rows under another filter still shows — so only the "(Blanks)" pseudo-value
        // drops when the popup no longer offers it. If that leaves nothing, no chip is rendered.
        const shown = new Set(getSetFilterValues(api, context, colId, staticOptionSets));
        const visibleChecked = values.filter((v) => v !== BLANKS_LABEL || shown.has(BLANKS_LABEL));
        if (!visibleChecked.length) return;
        chips.push({ colId, label: def.headerName || colId, count: visibleChecked.length, values: visibleChecked });
    });

    return { chips, selectAllColIds, filterModel };
};
